const JOURNAL_HEADER = '# Study Journal\n\n';
const STORAGE_KEY = 'astrocharts_study_journal';

/**
 * Parse key stats from the standard backend summaryLines array.
 * Returns an object with extracted values (strings, ready for display).
 */
export function parseStatsFromSummaryLines(summaryLines = []) {
  const stats = {};
  for (const line of summaryLines) {
    if (typeof line !== 'string') continue;
    const m = line.match(/^(.*?):\s*([\d.,-]+)/);
    if (!m) continue;
    const label = m[1].trim().toLowerCase();
    const value = m[2].trim();
    if (label.includes('up moves') && label.includes('#'))        stats.upMoves = value;
    if (label.includes('percent up'))                              stats.pctUp = value;
    if (label.includes('down moves') && label.includes('#'))      stats.downMoves = value;
    if (label.includes('percent down'))                            stats.pctDown = value;
    if (label.includes('long side gains') && !label.includes('stop') && !label.includes('largest'))
      stats.longGains = value;
    if (label.includes('short side gains') && !label.includes('stop') && !label.includes('largest'))
      stats.shortGains = value;
  }
  return stats;
}

/**
 * Build a single Markdown journal entry.
 *
 * @param {string} [reportText] a preformatted fixed-width report. When given it replaces the
 *   parsed stat lines rather than joining them — the report already states those figures, and
 *   printing both invites the two to disagree. It is fenced so the journal stays valid Markdown
 *   for the `.md` download; the panel itself already renders in a monospace font.
 */
export function buildJournalEntry({ studyLabel, summaryLines, futureDates, datePairCount, reportText }) {
  const now = new Date();
  const ts = now.toLocaleString('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  const stats = parseStatsFromSummaryLines(summaryLines);

  let md = '---\n';
  md += `### Study: ${studyLabel || 'Unnamed Study'}\n`;
  md += `**Date/Time:** ${ts}  \n`;

  if (reportText) {
    md += '\n```\n' + String(reportText).replace(/\s+$/, '') + '\n```\n';
    md += '---\n\n';
    return md;
  }

  if (datePairCount != null) md += `**Total Instances:** ${datePairCount}  \n`;
  if (stats.pctUp)       md += `**% Success (Long):** ${stats.pctUp}  \n`;
  if (stats.longGains)   md += `**Long Side Gains:** ${stats.longGains}  \n`;
  if (stats.shortGains)  md += `**Short Side Gains:** ${stats.shortGains}  \n`;
  if (stats.longGains && stats.shortGains) {
    const netGain = (parseFloat(stats.longGains) - parseFloat(stats.shortGains)).toFixed(2);
    md += `**Net Gain:** ${netGain}  \n`;
  }

  // Future dates
  const fdLines = Array.isArray(futureDates) ? futureDates : [];
  if (fdLines.length > 0) {
    md += '\n**Future Dates:**  \n';
    for (const fd of fdLines) {
      if (typeof fd === 'string') {
        md += `- ${fd.trim()}\n`;
      } else if (fd && fd.startDate && fd.endDate) {
        md += `- ${fd.startDate} to ${fd.endDate}\n`;
      }
    }
  }
  md += '---\n\n';
  return md;
}

/**
 * Most study entries kept. Older ones are dropped as new ones arrive, oldest first.
 *
 * Thirty. It was fifty; the reports written to the journal are much longer now that a study logs a
 * full statistics block rather than four summary lines, so a smaller cap holds a similar number of
 * bytes in the same localStorage budget.
 */
export const MAX_JOURNAL_ENTRIES = 30;

/**
 * Matches one study entry: from its `---` opener through the `---` that closes it.
 *
 * The journal is a single Markdown string, not a list, so entries have to be found by shape. The
 * opener is matched as `---` followed by `### Study:` rather than `---` alone, because `---` also
 * appears INSIDE the trendline summary block, and a looser pattern would treat that block's
 * divider as an entry boundary and cut it in half.
 */
const ENTRY_PATTERN = /^---\r?\n### Study: [\s\S]*?^---\r?\n\r?\n/gm;

/** The study entries in a journal, as {start, end} offsets, oldest first. */
function findEntries(md) {
  const found = [];
  ENTRY_PATTERN.lastIndex = 0;   // the regex is module-level and stateful with /g
  let m;
  while ((m = ENTRY_PATTERN.exec(md)) !== null) {
    found.push({ start: m.index, end: m.index + m[0].length });
  }
  return found;
}

/** How many study entries a journal holds. */
export function countJournalEntries(md) {
  return findEntries(md || '').length;
}

/**
 * Drops the oldest entries so at most `max` remain.
 *
 * Cuts the old entries OUT of the string rather than rebuilding it from the survivors, so
 * everything that is not a study entry survives untouched and in place: the header, anything the
 * customer typed themselves, and the delimited trendline summary block that another module owns
 * and rewrites by position. Reassembling from the entries alone would silently delete all of it.
 */
export function trimJournalEntries(md, max = MAX_JOURNAL_ENTRIES) {
  const base = md || '';
  const entries = findEntries(base);
  if (entries.length <= max) return base;

  // Splice from the end so earlier offsets stay valid as we cut.
  const doomed = entries.slice(0, entries.length - max).reverse();
  let out = base;
  doomed.forEach(({ start, end }) => {
    out = out.slice(0, start) + out.slice(end);
  });
  return out;
}

/**
 * Append a new entry to the existing journal markdown, keeping at most MAX_JOURNAL_ENTRIES.
 *
 * Trimmed on write rather than on read: the journal is persisted to localStorage, which has a
 * few-megabytes-per-origin quota shared with everything else the app stores. An unbounded journal
 * grows until `setItem` throws, and that failure is swallowed by design — so without a cap the
 * symptom would not be an error but entries silently ceasing to save.
 */
export function appendJournalEntry(existingMd, entryData) {
  let base = existingMd || JOURNAL_HEADER;
  if (!base.startsWith('# Study Journal')) {
    base = JOURNAL_HEADER + base;
  }
  return trimJournalEntries(base + buildJournalEntry(entryData));
}

/**
 * Return a fresh/empty journal.
 */
export function resetJournal() {
  return JOURNAL_HEADER;
}

/**
 * Storage key for one account's journal.
 *
 * Scoped by uid. The key used to be a single shared constant, so on a shared browser the journal
 * one customer wrote was loaded straight into the next customer's session — it survives a reload,
 * unlike in-memory state, so logging out did not clear it. Scoping keeps each account's journal
 * intact rather than wiping it on logout, which would lose the owner's own work.
 *
 * Entries written before scoping live under the bare key; they are migrated to the signed-in user
 * on first load so nobody loses a journal to this change.
 */
function keyFor(userId) {
  return userId ? `${STORAGE_KEY}:${userId}` : STORAGE_KEY;
}

/**
 * Load a user's journal from localStorage.
 *
 * @param {string} [userId] uid of the signed-in user; omit for the legacy unscoped journal.
 */
export function loadJournal(userId) {
  try {
    const scoped = localStorage.getItem(keyFor(userId));
    if (scoped !== null) return scoped;

    // One-time migration: adopt a pre-scoping journal, then leave the old key alone so a
    // half-finished migration cannot destroy it.
    if (userId) {
      const legacy = localStorage.getItem(STORAGE_KEY);
      if (legacy !== null) {
        localStorage.setItem(keyFor(userId), legacy);
        return legacy;
      }
    }
    return JOURNAL_HEADER;
  } catch {
    return JOURNAL_HEADER;
  }
}

/**
 * Save a user's journal to localStorage.
 *
 * @param {string} md journal markdown
 * @param {string} [userId] uid of the signed-in user
 */
export function saveJournal(md, userId) {
  try {
    localStorage.setItem(keyFor(userId), md);
  } catch {
    // storage full or unavailable — silently fail
  }
}
